import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import { entities, modules, useFeature } from "byzantine";
import type { InvoiceRecipientMatchDisplayState } from "byzantine/src/dbbl/businessLogic/entities/invoices/helpers";
import * as formAdapters from "../../src/adapters";
import {
  RecipientDrawer,
  DRAWER_TYPES,
  SECTION_NAMES,
} from "../RecipientDrawer";
import type { InvoiceRecipientInitialValues } from "../RecipientDrawer";
import { RecipientDrawer as NewRecipientDrawer } from "../../src/modules/recipientDrawer";
import RecipientSelector from "./RecipientSelector";
import RecipientCard from "./invoice/RecipientCard";
import type { RecipientCardFields } from "./invoice/RecipientCard";

type FallbackCard = {
  title: string | null;
  fields: RecipientCardFields | null;
  state?: InvoiceRecipientMatchDisplayState;
  onEdit?: () => void;
  onClear: () => void;
};

type RecipientSelectorWithDrawerProps = {
  label: string;
  // called after the shared onChange runs, so callers can layer extra behavior
  // (e.g. resetting the ACH payment form when the recipient changes)
  onChange?: (recipientId: API.RecipientId) => void;
  showSelectedCard?: boolean;
  error?: string;
  cardState?: InvoiceRecipientMatchDisplayState;
  // Optionally control the drawer from the parent, for callers whose
  // "open the drawer" affordance lives outside this component. Left undefined,
  // the drawer manages its own open state.
  isDrawerOpen?: boolean;
  onDrawerOpenChange?: (isOpen: boolean) => void;
  // values to seed the drawer's forms with
  initialValuesFromInvoice?: InvoiceRecipientInitialValues;
  // fires when the drawer creates a recipient, i.e. nothing was selected when it opened
  onRecipientCreated?: (recipientId: API.RecipientId) => void;
  // keep a newly created recipient selected even if it still has no ACH
  // destination, instead of dropping the selection
  keepRecipientWithoutAchDetails?: boolean;
  // badge the drawer's profile/ACH sections as still needing attention
  drawerNeedsReview?: boolean;
  // shown instead of the dropdown when no recipient is selected
  fallbackCard?: FallbackCard;
};

/**
 * Shared recipient dropdown + drawer used by the ACH payment and invoice flows.
 *
 * Binds the `ACHPaymentRecipientForm` `selectedRecipientId` field, and mirrors
 * the ACH flow behavior for recipients missing ACH details: auto-opens the
 * recipient drawer on the ACH section, and clears + warns if details are still
 * missing after the drawer closes.
 */
const RecipientSelectorWithDrawer = ({
  label,
  onChange: onChangeProp,
  showSelectedCard = false,
  error,
  cardState,
  isDrawerOpen: isDrawerOpenProp,
  onDrawerOpenChange,
  initialValuesFromInvoice,
  onRecipientCreated,
  keepRecipientWithoutAchDetails = false,
  drawerNeedsReview = false,
  fallbackCard,
}: RecipientSelectorWithDrawerProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [isDrawerOpenInternal, setIsDrawerOpenInternal] = useState(false);
  const isDrawerOpen = isDrawerOpenProp ?? isDrawerOpenInternal;
  const setIsDrawerOpen = (open: boolean) => {
    if (isDrawerOpenProp === undefined) setIsDrawerOpenInternal(open);
    onDrawerOpenChange?.(open);
  };
  const [customAlertCopy, setCustomAlertCopy] = useState<string>("");
  const isNewDrawerEnabled = useFeature().featureEnabled("recipient_drawer");
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const recipientForm = entities.recipients.ACHPaymentRecipientForm.useForm();
  const hasManageRecipientPermission =
    modules.achPayments.useManageRecipientPermission();
  // if user doesn't have manage recipient permissions, just show them recipients they can only send ach payments to
  const recipients = modules.achPayments.usePermissionedRecipients();
  const {
    values: { selectedRecipientId },
  } = recipientForm;
  const { onChange, ...restOfFieldProps } = formAdapters.fieldWithOnChange(
    recipientForm,
    "selectedRecipientId"
  );

  const isRecipientWithACHDestinationMissing =
    selectedRecipient && !selectedRecipient.ach_destination;

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };
  const closeDrawer = () => setIsDrawerOpen(false);

  // Selecting a recipient without ACH details normally yanks the drawer open on
  // the ACH section. Callers that render their own missing-details affordance
  // opt out and drive the drawer themselves.
  useEffect(() => {
    if (!isRecipientWithACHDestinationMissing) return;
    if (keepRecipientWithoutAchDetails) return;
    setCustomAlertCopy(l10n.getString("ach-payment-ach-details-banner"));
    setIsDrawerOpen(true);
  }, [
    isRecipientWithACHDestinationMissing,
    keepRecipientWithoutAchDetails,
    l10n,
  ]);

  const handleChange = (recipientId: API.RecipientId) => {
    onChange(recipientId);
    onChangeProp?.(recipientId);
  };

  const handleCloseDrawer = (recipientId?: API.RecipientId) => {
    // nothing selected when the drawer opened means it was an add session, so
    // any recipient coming back was created just now
    const wasAddSession = !selectedRecipientId;
    const updatedRecipient = recipients.find((r) => r.id === recipientId);
    if (updatedRecipient && !updatedRecipient?.ach_destination) {
      // the recipient exists but can't be paid yet. Callers that show their own
      // "details missing" affordance keep it selected; everyone else drops it so
      // an unpayable recipient can't be carried into the payment step.
      if (keepRecipientWithoutAchDetails) {
        handleChange(recipientId as API.RecipientId);
      } else {
        handleChange("" as API.RecipientId);
      }
      sendNotification({
        type: "negative",
        text: l10n.getString("add-ach-details-banner"),
      });
    } else if (recipientId) {
      handleChange(recipientId);
    }

    if (wasAddSession && recipientId) onRecipientCreated?.(recipientId);

    closeDrawer();
    setCustomAlertCopy("");
  };

  const handleClearRecipient = () => {
    handleChange("" as API.RecipientId);
  };

  // Normally a recipient only earns a card once it's payable. Callers handling
  // incomplete recipients still want the card, so the "details missing" state
  // is visible instead of an innocuous-looking dropdown.
  const shouldShowCard =
    showSelectedCard &&
    !!selectedRecipient &&
    (!!selectedRecipient.ach_destination || keepRecipientWithoutAchDetails);

  // card for the selected recipient, else the caller's fallback card, else the
  // dropdown. The drawer is rendered in every case, so opening it never costs
  // the user the card they were looking at.
  let recipientDisplay;
  if (shouldShowCard) {
    recipientDisplay = (
      <RecipientCard
        title={selectedRecipient.name}
        {...(cardState ? { state: cardState } : {})}
        fields={
          selectedRecipient.ach_destination
            ? {
                financialInstitution:
                  selectedRecipient.ach_destination.institution_name,
                accountNumber: selectedRecipient.ach_destination.account_number,
                routingNumber: selectedRecipient.ach_destination.routing_number,
              }
            : null
        }
        // an unpayable recipient already has a "review details" call to
        // action driving the drawer, so the card only offers to clear it
        {...(selectedRecipient.ach_destination ? { onEdit: toggleDrawer } : {})}
        onClear={handleClearRecipient}
      />
    );
  } else if (fallbackCard) {
    recipientDisplay = (
      <RecipientCard
        title={fallbackCard.title}
        fields={fallbackCard.fields}
        {...(fallbackCard.state ? { state: fallbackCard.state } : {})}
        {...(fallbackCard.onEdit ? { onEdit: fallbackCard.onEdit } : {})}
        onClear={fallbackCard.onClear}
      />
    );
  } else {
    recipientDisplay = (
      <RecipientSelector
        recipients={recipients}
        field="recipientId"
        label={label}
        onAddRecipientClick={toggleDrawer}
        onChange={handleChange}
        displayBottomAction={hasManageRecipientPermission}
        {...restOfFieldProps}
        {...(error ? { error } : {})}
      />
    );
  }

  return (
    <>
      {recipientDisplay}
      {isNewDrawerEnabled ? (
        <NewRecipientDrawer
          isOpen={isDrawerOpen}
          flow="ach"
          onClose={({ recipient }) => handleCloseDrawer(recipient?.id)}
          defaultValues={initialValuesFromInvoice}
          reviewDefaultValues={drawerNeedsReview}
          {...(selectedRecipientId
            ? { mode: "edit", recipientId: selectedRecipientId }
            : { mode: "create" })}
        />
      ) : (
        <RecipientDrawer
          // never "" — an empty id still counts as a change against undefined,
          // which cascades a form reset through the drawer's sections
          recipientId={selectedRecipientId || undefined}
          handleClose={handleCloseDrawer}
          isOpen={isDrawerOpen}
          fromACHFlow={true}
          drawerType={
            selectedRecipientId ? DRAWER_TYPES.edit : DRAWER_TYPES.add
          }
          customAlertCopy={customAlertCopy}
          defaultSectionToOpen={
            customAlertCopy || isRecipientWithACHDestinationMissing
              ? SECTION_NAMES.ach
              : undefined
          }
          {...(initialValuesFromInvoice ? { initialValuesFromInvoice } : {})}
          needsReview={drawerNeedsReview}
        />
      )}
    </>
  );
};

export default RecipientSelectorWithDrawer;
